/* eslint-disable no-empty */
import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Reveal, GoldDivider, StaggerHeadline } from '../gate33-program/components';
import { CATERING_MODULES, CATERING_OVERVIEW } from './catering-modules';
import { CateringBottomDock } from './CateringShell';

const ROOT = '/gate33-catering-and-operations-training';

// Premium cinematic hero — same private aviation editorial as 3-day program
const HERO_IMG =
  'https://images.unsplash.com/photo-1619985631958-d44cea12acfc?w=2400&q=85&auto=format&fit=crop';

// =====================================================================
// Local TrainerSection — mirrors the one in /gate33-program/
// =====================================================================
const TrainerSection = ({ eyebrow, children, testId }) => (
  <div className="g33-trainer-block" data-testid={testId}>
    <div className="g33-trainer-tag">
      <span className="g33-trainer-tag-dot" aria-hidden="true" />
      Trainer voice · Nicolas
    </div>
    <div className="g33-trainer-eyebrow">{eyebrow}</div>
    <div className="g33-trainer-content">{children}</div>
  </div>
);

// Render multi-paragraph narrative ("\n\n" separated)
const renderNarrative = (text) => {
  if (!text) return null;
  const paras = String(text).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return paras.map((p, i) => (
    <p
      key={i}
      className="g33-body"
      style={{ margin: i === 0 ? '0 0 24px' : '0 0 24px' }}
    >
      {p}
    </p>
  ));
};

// =====================================================================
// LANDING — clone of the gate33-program landing, adapted for 1-day flow
// =====================================================================
export const CateringLanding = () => {
  return (
    <>
      {/* HERO — split layout, mirrors /gate33-program/ */}
      <section className="g33-module-hero" style={{ paddingTop: 140 }}>
        <div className="g33-module-hero-grid">
          <Reveal y={32}>
            <div className="g33-module-hero-image">
              <motion.img
                src={HERO_IMG}
                alt="Gate 33 — Operations & Catering Management"
                initial={{ scale: 1.06 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </div>
          </Reveal>

          <div className="g33-module-hero-content">
            <Reveal delay={0.1}>
              <div className="g33-module-hero-eyebrow">
                <span className="g33-module-hero-eyebrow-rule" />
                Gate 33 · Confidential program
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <StaggerHeadline
                text={`${CATERING_OVERVIEW.title}.`}
                className="g33-display g33-module-hero-title"
              />
            </Reveal>

            <Reveal delay={0.4}>
              <p className="g33-module-hero-hook">{CATERING_OVERVIEW.hook}</p>
            </Reveal>

            <Reveal delay={0.5}>
              <div className="g33-module-stat-grid" data-testid="catering-stats">
                {CATERING_OVERVIEW.stats.map((s, i) => (
                  <div className="g33-module-stat-box" key={i}>
                    <div className="g33-module-stat-value">
                      {s.value}
                      {s.suffix && (
                        <span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>
                          {s.suffix}
                        </span>
                      )}
                    </div>
                    <div className="g33-module-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <Link
                to={`${ROOT}/module/${CATERING_MODULES[0].slug}`}
                className="g33-btn-primary g33-module-hero-cta"
                data-testid="catering-cta-begin"
              >
                Begin Module 01 <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO — "Course overview" — full-width editorial */}
      <section style={{ padding: '120px 32px', maxWidth: 1280, margin: '0 auto' }}>
        <Reveal>
          <div className="g33-eyebrow" style={{ marginBottom: 16 }}>Course overview</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="g33-display"
            style={{
              fontSize: 'clamp(40px, 5vw, 64px)',
              marginBottom: 16,
              maxWidth: 900,
            }}
          >
            From flight brief to ready-to-serve cabin.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p
            style={{
              color: 'var(--g33-charcoal)',
              fontSize: 17,
              lineHeight: 1.7,
              maxWidth: 760,
              marginBottom: 64,
            }}
          >
            One intensive day. Ten operational modules. The complete journey of a
            private flight catering, taught the way premium operators actually run it.
            You will not just learn what to order — you will understand the logic
            behind every decision.
          </p>
        </Reveal>

        <GoldDivider />
      </section>

      {/* TEN MODULES — editorial list, same density as Day Overview */}
      <section style={{ padding: '0 32px 160px', maxWidth: 1280, margin: '0 auto' }}>
        <Reveal>
          <div className="g33-eyebrow" style={{ marginBottom: 16 }}>The 10-module program</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="g33-display"
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              marginBottom: 64,
              maxWidth: 800,
            }}
          >
            Open any module to begin.
          </h2>
        </Reveal>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {CATERING_MODULES.map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.04}>
              <li>
                <Link
                  to={`${ROOT}/module/${m.slug}`}
                  data-testid={`catering-module-row-${m.n}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr auto',
                    gap: 32,
                    padding: '40px 0',
                    borderBottom: '1px solid var(--g33-ligne)',
                    textDecoration: 'none',
                    color: 'inherit',
                    alignItems: 'baseline',
                    transition: 'background 0.3s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 232, 218, 0.4)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div
                    style={{
                      fontFamily: 'var(--g33-font-display)',
                      fontWeight: 300,
                      fontSize: 'clamp(48px, 5vw, 72px)',
                      color: 'var(--g33-or)',
                      lineHeight: 1,
                    }}
                  >
                    {String(m.n).padStart(2, '0')}
                  </div>
                  <div>
                    <h3
                      className="g33-display"
                      style={{ fontSize: 26, marginBottom: 8, lineHeight: 1.2 }}
                    >
                      {m.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: 'var(--g33-font-display)',
                        fontStyle: 'italic',
                        color: 'var(--g33-charcoal)',
                        fontSize: 16,
                        lineHeight: 1.5,
                      }}
                    >
                      {m.hook}
                    </p>
                  </div>
                  <div className="g33-eyebrow" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {m.duration_min} min
                  </div>
                </Link>
              </li>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* WHAT YOU WALK AWAY WITH */}
      <section
        style={{ background: 'var(--g33-cream-warm)', padding: '120px 32px' }}
        data-testid="catering-walkaway"
      >
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal>
            <div className="g33-eyebrow" style={{ marginBottom: 16 }}>What you walk away with</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              className="g33-display"
              style={{
                fontSize: 'clamp(36px, 4.5vw, 56px)',
                marginBottom: 24,
                maxWidth: 900,
              }}
            >
              An operational foundation deployable on the very next flight.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p
              style={{
                color: 'var(--g33-charcoal)',
                fontSize: 18,
                lineHeight: 1.7,
                maxWidth: 820,
              }}
            >
              {CATERING_OVERVIEW.walkAway}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: '140px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <Reveal>
            <div className="g33-eyebrow" style={{ marginBottom: 24 }}>Ready</div>
            <h2
              className="g33-display"
              style={{ fontSize: 'clamp(36px, 4.5vw, 56px)', marginBottom: 40 }}
            >
              The complete course is open.
            </h2>
            <Link
              to={`${ROOT}/module/${CATERING_MODULES[0].slug}`}
              className="g33-btn-primary"
              data-testid="catering-cta-final"
            >
              Begin Module 01 <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

// =====================================================================
// MODULE PAGE — mirrors /gate33-program/ Module 1 layout exactly
// =====================================================================
export const CateringModulePage = () => {
  const { slug } = useParams();
  const m = CATERING_MODULES.find((x) => x.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!m) return <Navigate to={ROOT} replace />;

  return (
    <>
      {/* HERO — gate33.co-style split layout (image left + content right) */}
      <section className="g33-module-hero">
        <div className="g33-module-hero-grid">
          {/* IMAGE — left column */}
          <Reveal y={32}>
            <div className="g33-module-hero-image" style={{ position: 'relative' }}>
              <motion.img
                src={m.hero_image}
                alt={m.title}
                initial={{ scale: 1.06 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </div>
          </Reveal>

          {/* CONTENT — right column */}
          <div className="g33-module-hero-content">
            <Reveal delay={0.1}>
              <div className="g33-module-hero-eyebrow">
                <span className="g33-module-hero-eyebrow-rule" />
                Operations &amp; Catering · 1-day
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <h1 className="g33-display g33-module-hero-title" data-testid="catering-module-title">
                {m.title}
              </h1>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="g33-module-hero-hook" data-testid="catering-module-hook">
                {m.hook}
              </p>
            </Reveal>

            {/* STAT BOXES */}
            <Reveal delay={0.45}>
              <div className="g33-module-stat-grid" data-testid="catering-module-stats">
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    {String(m.n).padStart(2, '0')}
                  </div>
                  <div className="g33-module-stat-label">Module</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    {m.duration_min}
                    <span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>min</span>
                  </div>
                  <div className="g33-module-stat-label">Duration</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    {m.n}
                    <span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>/10</span>
                  </div>
                  <div className="g33-module-stat-label">Sequence</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <a
                href="#objective"
                className="g33-btn-primary g33-module-hero-cta"
                data-testid="catering-module-cta"
              >
                Discover the module <ArrowRight size={14} />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBJECTIVE — TrainerSection block */}
      <section
        id="objective"
        style={{ padding: '120px 32px', maxWidth: 1080, margin: '0 auto' }}
      >
        <Reveal>
          <TrainerSection eyebrow="Objective" testId="catering-objective-trainer">
            <p className="g33-body" style={{ margin: 0 }}>
              {m.objective}
            </p>
          </TrainerSection>
        </Reveal>
      </section>

      {/* TRAINER NARRATIVE — cream-soft backdrop */}
      <section style={{ background: 'var(--g33-cream-soft)', padding: '120px 32px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <Reveal>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--g33-or)',
                marginBottom: 24,
              }}
            >
              <span className="g33-trainer-tag-dot" aria-hidden="true" />
              Trainer voice · Nicolas
            </div>
            <div
              className="g33-eyebrow"
              style={{ marginBottom: 24, color: 'var(--g33-or-deep)' }}
            >
              Trainer narrative
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="catering-module-narrative">{renderNarrative(m.narrative)}</div>
          </Reveal>
        </div>
      </section>

      {/* PULL QUOTE — optional */}
      {m.pullQuote && (
        <section style={{ padding: '120px 32px' }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <Reveal>
              <blockquote
                className="g33-pull-quote"
                data-testid="catering-module-pullquote"
              >
                {m.pullQuote}
              </blockquote>
            </Reveal>
          </div>
        </section>
      )}

      {/* IMAGE PLACEHOLDERS — discreet markers for the trainer */}
      {m.placeholders && m.placeholders.length > 0 && (
        <section
          style={{ padding: '0 32px 80px', maxWidth: 1080, margin: '0 auto' }}
          data-testid={`catering-image-placeholders-${m.n}`}
        >
          <div
            className="g33-eyebrow"
            style={{ marginBottom: 16, color: 'var(--g33-grey-warm)' }}
          >
            Image placeholders for this module
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {m.placeholders.map((ph) => (
              <div
                key={ph.id}
                style={{
                  aspectRatio: '4 / 3',
                  border: '1px dashed var(--g33-or)',
                  background: 'rgba(201, 169, 97, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 24,
                  textAlign: 'center',
                }}
              >
                <div
                  className="g33-eyebrow"
                  style={{ color: 'var(--g33-or-deep)', marginBottom: 8 }}
                >
                  {ph.id}
                </div>
                <div style={{ fontSize: 13, color: 'var(--g33-charcoal)' }}>
                  {ph.caption}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* INLINE NAV (Back to program + Next module) */}
      <section style={{ padding: '40px 32px 160px', maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 24,
            paddingTop: 32,
            borderTop: '1px solid var(--g33-ligne)',
          }}
        >
          <Link
            to={ROOT}
            className="g33-btn-ghost"
            data-testid="catering-back-to-program"
          >
            ← Back to program
          </Link>
          {m.n < CATERING_MODULES.length && (
            <Link
              to={`${ROOT}/module/${CATERING_MODULES[m.n].slug}`}
              className="g33-btn-primary"
              data-testid="catering-inline-next"
            >
              Next module <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </section>

      <CateringBottomDock current={m} />
    </>
  );
};
